import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Waves, CloudRain, Flame, Bell, Timer, Download, Volume2, Droplets, Wind, Bird, Moon, CloudLightning, Music, Activity, Sparkles, Flower2, Trees, Radio, Cloud, Lamp, Heart } from "lucide-react";
import { PageHero, SectionTitle } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { LanternComposer } from "@/components/LanternComposer";
import { Slider } from "@/components/ui/slider";
import { SanctuaryEngine } from "@/lib/audioEngine";
import { IMG } from "@/lib/images";
import { api, API } from "@/lib/api";
import { STORE_VISIBLE } from "@/lib/flags";

const SOUNDS = [
  { id: "ocean", name: "Ocean Surf", desc: "Rolling waves breathing in and out.", icon: Waves, color: "#0E7490" },
  { id: "rain", name: "Soft Rain", desc: "Gentle rainfall on a quiet afternoon.", icon: CloudRain, color: "#2B527E" },
  { id: "hearth", name: "Crackling Fire", desc: "A cozy hearth popping and glowing.", icon: Flame, color: "#B45309" },
  { id: "stream", name: "Babbling Brook", desc: "A clear stream over smooth stones.", icon: Droplets, color: "#0891B2" },
  { id: "wind", name: "Gentle Wind", desc: "Soft breezes drifting through the valley.", icon: Wind, color: "#64748B" },
  { id: "forest", name: "Forest Birdsong", desc: "A dawn chorus deep in the woods.", icon: Bird, color: "#15803D" },
  { id: "night", name: "Night Crickets", desc: "A calm summer night's gentle hum.", icon: Moon, color: "#4338CA" },
  { id: "thunder", name: "Distant Thunder", desc: "Faraway rumbles over steady rain.", icon: CloudLightning, color: "#475569" },
  { id: "chimes", name: "Wind Chimes", desc: "Delicate chimes on a soft breeze.", icon: Music, color: "#9333EA" },
  { id: "bowl", name: "Singing Bowl", desc: "Resonant chimes to anchor each breath.", icon: Bell, color: "#8C6D23" },
  { id: "heartbeat", name: "Resting Heartbeat", desc: "A slow, steady beat to settle you.", icon: Activity, color: "#DC2626" },
  { id: "waves432", name: "432Hz Calm Waves", desc: "A warm, grounding healing tone.", icon: Sparkles, color: "#0E7490" },
  { id: "meadow", name: "Summer Meadow", desc: "Breezy grasses and distant songbirds.", icon: Flower2, color: "#CA8A04" },
  { id: "rainforest", name: "Rainforest", desc: "Rain, birds and insects in harmony.", icon: Trees, color: "#047857" },
  { id: "drone", name: "Deep Om Drone", desc: "A low meditative drone for stillness.", icon: Radio, color: "#7C3AED" },
  { id: "sleep", name: "Deep Sleep Noise", desc: "Soft, steady noise to drift away.", icon: Cloud, color: "#334155" },
];

const TIMERS = [0, 5, 10, 20];

const ClipCard = ({ v, i }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const toggle = () => {
    const el = ref.current;
    if (el.paused) { setPlaying(true); el.play().catch(() => setPlaying(false)); } else { el.pause(); setPlaying(false); }
  };
  return (
    <button onClick={toggle} className="relative rounded-2xl overflow-hidden h-56 card-tactile text-left w-full" data-testid={`visual-clip-${i}`} aria-label={`${playing ? "Pause" : "Play"} ${v.title}`}>
      <video ref={ref} src={`${API}/media/${v.video_path}`} poster={v.poster_path ? `${API}/media/${v.poster_path}` : undefined} loop muted playsInline preload="none" className="absolute inset-0 w-full h-full object-cover" data-testid={`visual-clip-video-${i}`} />
      <div className="absolute inset-0 bg-gradient-to-t from-bsn-midnight/85 via-bsn-midnight/10 to-transparent" />
      <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full bg-bsn-midnight/70 text-bsn-champagne">
        {playing ? <><Pause className="w-3 h-3" /> Playing</> : <><Play className="w-3 h-3" /> Tap to Play</>}
      </span>
      {!playing && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-14 h-14 rounded-full bg-bsn-midnight/70 border border-bsn-gold/60 flex items-center justify-center"><Play className="w-6 h-6 text-bsn-gold ml-0.5" /></span>
        </span>
      )}
      <div className="absolute bottom-0 left-0 p-5">
        <p className="font-serif text-xl font-semibold text-bsn-parchment">{v.title}</p>
        <p className="text-sm text-bsn-ice/80">{v.subtitle}</p>
      </div>
    </button>
  );
};

export default function Sanctuary() {
  const engineRef = useRef(null);
  const [active, setActive] = useState(null);
  const [volume, setVolume] = useState(60);
  const [timer, setTimer] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [videoBroken, setVideoBroken] = useState(false);
  const [clips, setClips] = useState([]);

  useEffect(() => { api.get("/sanctuary/clips").then(({ data }) => setClips(data.clips)).catch(() => {}); }, []);

  useEffect(() => {
    engineRef.current = new SanctuaryEngine();
    return () => { engineRef.current?.stop(); engineRef.current?.pauseSilent(); };
  }, []);

  const toggle = async (id) => {
    const eng = engineRef.current;
    if (active === id) { eng.stop(); eng.pauseSilent(); setActive(null); return; }
    await eng.play(id);
    eng.setVolume(volume / 100);
    setVideoBroken(false);
    setActive(id);
  };

  useEffect(() => { engineRef.current?.setVolume(volume / 100); }, [volume]);

  // Always try the video first when the scene changes (reset any prior fallback).
  useEffect(() => { setVideoBroken(false); }, [active]);

  // Calm timer
  useEffect(() => {
    if (!remaining) return;
    const iv = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { engineRef.current?.stop(); setActive(null); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [remaining]);

  const startTimer = (mins) => { setTimer(mins); setRemaining(mins * 60); };
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const activeName = SOUNDS.find((s) => s.id === active)?.name || "";

  return (
    <div data-testid="sanctuary-page">
      <PageHero
        eyebrow="Calming Media Sanctuary"
        title="A quiet place to simply breathe"
        subtitle="Live-generated soothing soundscapes and tranquil visuals. Press play, soften your shoulders, and let the noise of the day fall away."
        image={IMG.heroDawn}
        testid="sanctuary-hero"
      />

      <section className="max-w-6xl mx-auto px-4 py-16 sm:py-20">
        {/* Player console */}
        <Reveal>
          <div className="bg-bsn-midnight rounded-3xl p-6 sm:p-8 border border-bsn-gold/25 shadow-[0_24px_60px_rgba(11,25,44,0.25)]">
            <div className="rounded-2xl overflow-hidden mb-6 relative h-56 sm:h-72 bg-bsn-navy">
              {active ? (
                videoBroken ? (
                  <img src={`/sanctuary_videos/${active}.jpg`} alt={activeName} className="absolute inset-0 w-full h-full object-cover kenburns" onError={(e) => { e.currentTarget.src = IMG.heroDawn; }} />
                ) : (
                  <video key={active} src={`/sanctuary_videos/${active}.mp4`} poster={`/sanctuary_videos/${active}.jpg`}
                    autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover"
                    onError={() => setVideoBroken(true)} data-testid="sanctuary-video" />
                )
              ) : (
                <img src={IMG.lakeDock} alt="Tranquil visual" className="absolute inset-0 w-full h-full object-cover opacity-60 kenburns" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-bsn-midnight/75 via-transparent to-bsn-midnight/10 pointer-events-none" />
              {active ? (
                <div className="absolute bottom-4 left-5 flex items-center gap-2" data-testid="sanctuary-now-playing">
                  <span className="inline-flex items-center gap-2 bg-bsn-midnight/60 backdrop-blur-sm text-bsn-champagne px-4 py-1.5 rounded-full text-sm font-semibold border border-bsn-gold/30">
                    <span className="w-2 h-2 rounded-full bg-bsn-gold animate-pulse" /> {activeName}
                  </span>
                </div>
              ) : (
                <p className="absolute inset-0 flex items-center justify-center font-serif text-xl text-bsn-champagne/90">Choose a sound to begin</p>
              )}
            </div>

            <div className="flex items-center gap-4">
              <Volume2 className="w-5 h-5 text-bsn-gold shrink-0" />
              <Slider value={[volume]} onValueChange={(v) => setVolume(v[0])} max={100} step={1} className="flex-1" data-testid="sanctuary-volume" />
              <span className="font-mono text-xs text-bsn-ice/60 w-10 text-right">{volume}%</span>
            </div>
          </div>
        </Reveal>

        {/* Sound board */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="sound-board">
          {SOUNDS.map((s, i) => (
            <Reveal key={s.id} delay={i * 80}>
              <button onClick={() => toggle(s.id)}
                className={`w-full text-left rounded-2xl p-6 transition-all duration-300 flex items-center gap-4 ${active === s.id ? "bg-bsn-navy text-bsn-parchment ring-2 ring-bsn-gold" : "bg-white card-tactile hover:-translate-y-1"}`}
                data-testid={`audio-sound-${s.id}`}>
                <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: active === s.id ? "rgba(212,175,55,0.2)" : `${s.color}15` }}>
                  {active === s.id ? <Pause className="w-6 h-6 text-bsn-gold" /> : <s.icon className="w-6 h-6" style={{ color: s.color }} />}
                </div>
                <div className="flex-1">
                  <p className={`font-serif text-lg font-semibold ${active === s.id ? "text-bsn-champagne" : "text-bsn-midnight"}`}>{s.name}</p>
                  <p className={`text-sm ${active === s.id ? "text-bsn-ice/70" : "text-bsn-sapphire/70"}`}>{s.desc}</p>
                </div>
                {active === s.id ? <Pause className="w-5 h-5 text-bsn-gold" /> : <Play className="w-5 h-5 text-bsn-bronze" />}
              </button>
            </Reveal>
          ))}
        </div>

        {/* Calm timer */}
        <Reveal>
          <div className="mt-8 bg-bsn-canvas rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4 card-tactile">
            <div className="flex items-center gap-3">
              <Timer className="w-6 h-6 text-bsn-bronze" />
              <div>
                <p className="font-serif text-lg font-semibold text-bsn-midnight">Gentle Calm Timer</p>
                <p className="text-sm text-bsn-sapphire/70">{remaining > 0 ? `Fading out in ${fmt(remaining)}` : "Let the sound rest you, then fade softly"}</p>
              </div>
            </div>
            <div className="flex gap-2" data-testid="sanctuary-timer">
              {TIMERS.map((m) => (
                <button key={m} onClick={() => startTimer(m)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${timer === m && remaining > 0 ? "bg-bsn-midnight text-bsn-parchment" : "bg-white text-bsn-sapphire hover:bg-bsn-gold/15"}`}
                  data-testid={`timer-${m}`}>
                  {m === 0 ? "Off" : `${m}m`}
                </button>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* Send a Lantern */}
      <section id="lantern" className="bg-bsn-midnight text-bsn-parchment border-t border-bsn-gold/20 scroll-mt-20" data-testid="lantern-section">
        <div className="max-w-3xl mx-auto px-4 py-16 sm:py-20">
          <div className="text-center mb-9">
            <div className="w-12 h-12 rounded-xl bg-bsn-gold/15 flex items-center justify-center mx-auto mb-4">
              <Lamp className="w-6 h-6 text-bsn-gold" />
            </div>
            <p className="eyebrow text-bsn-gold">A Small Act of Kindness</p>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-bsn-champagne mt-2">Light a lantern for someone</h2>
            <p className="text-bsn-ice/80 mt-4 leading-relaxed max-w-xl mx-auto">
              Thinking of someone who could use a little light? Choose a calm scene, write them a gentle note,
              and send a private link — a quiet moment made just for them, without sharing the whole page.
            </p>
          </div>
          <Reveal>
            <LanternComposer defaultScene={active || "hearth"} />
          </Reveal>
          <p className="text-center text-xs text-bsn-ice/50 mt-5 flex items-center justify-center gap-1.5">
            <Heart className="w-3.5 h-3.5 text-bsn-gold/70" /> Your note is private — only someone with the link can open it.
          </p>
        </div>
      </section>

      {/* Visual clips & downloads */}
      <section className="bg-bsn-canvas border-y border-bsn-gold/20">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <SectionTitle eyebrow="Visual Sanctuary & Downloads" title="Gentle companions to keep close" subtitle="Soft, comforting moments with puppies, a sleepy cat, a loyal dog, and a gentle bunny — a little warmth for your heart. Tap any scene to play it." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {clips.map((v, i) => <ClipCard key={v.id} v={v} i={i} />)}
          </div>
          <p className="text-center text-xs text-bsn-sapphire/60 mt-6">
            Tap a scene to play or pause{STORE_VISIBLE ? " · high-resolution MP4 video clips are included in the downloadable Sanctuary Suite." : "."}
          </p>
          {STORE_VISIBLE && <div className="text-center mt-10">
            <Link to="/shop" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full btn-gold font-semibold transition-all" data-testid="sanctuary-download-cta">
              <Download className="w-5 h-5" /> Get the Audio & MP4 Suite
            </Link>
          </div>}
        </div>
      </section>
    </div>
  );
}
